/**
 * The L1 composition panels on `/ai/overview`: what the estate is made of, by
 * ARM `kind`, by region, and by resource *type*.
 *
 * ## Nothing here is an enum, and that is the point (§8)
 *
 * `kind` is whatever string ARM returned. The estate holds `AIServices` (14),
 * `OpenAI` (4) and `FormRecognizer` (1) today — **three** kinds, and the third is
 * the one that matters: the board discovers by resource *type*, so a kind
 * Microsoft ships next month appears here on its own with no deploy. So there is
 * no hardcoded kind list anywhere in this file: the donut's segments, its colour
 * assignment and its legend are all derived from the payload, and an account with
 * no kind at all arrives as the explicit `unknown` bucket rather than being
 * guessed at.
 *
 * The colour assignment is built from the payload's **full** kind list, ordered
 * alphabetically, so it is independent of payload order and a filter elsewhere on
 * the board cannot repaint the survivors (§3.3).
 *
 * `by_type` is the discovery net itself — the census across the AI provider
 * prefixes, including types nobody enumerated. It is a small table on an L1 board
 * on purpose: it is an aggregate over the whole estate (a handful of rows), and
 * it is the panel that would reveal a resource type this board does not otherwise
 * know how to render.
 *
 * ## Null vs zero, again
 *
 * `deployments` is nullable on both `AiKindCount` and `AiRegionCount`, for the
 * same reason as everywhere else: Resource Graph does not reliably index the
 * deployment child type. So the deployment column renders a `Reading`, never a
 * bare number, and a null renders as an em dash with a badge rather than as a
 * kind with nothing deployed.
 */

import { Chart } from "@norican/dataviz";
import { Icon } from "@norican/icons";
import { Badge, DataGrid, EmptyState, Statistic, Typography } from "@norican/react";

import { ChartCard } from "@/components/board";
import type { AiKindCount, AiRegionCount, AiTypeCount } from "@/lib/api";
import { identityConfig } from "@/lib/charts/config";

import { PanelCard, ReadingCell, SectionHeading } from "./panel-card";
import { COMPOSITION_ANCHOR, TWIN_IDS } from "./params";
import {
  deploymentsReading,
  formatCount,
  formatOfTotal,
  formatPercent,
  MUTED_FILL,
  UNKNOWN_KIND,
} from "./status";

/** How many regions the L1 bar chart ranks before folding the tail (§1). */
const REGION_LIMIT = 10;

/** Donut segment cap before the tail is folded into one `Other` slice (§2). */
const DONUT_LIMIT = 5;

interface AiCompositionSectionProps {
  byKind: readonly AiKindCount[];
  byRegion: readonly AiRegionCount[];
  byType: readonly AiTypeCount[];
  /** MCP servers identified, for the pointer at the foot of the section. */
  mcpDiscovered: number | null;
  /** Of those, how many ARM does not report as running. */
  mcpNotRunning: number | null;
  collectedAt: string | null;
  staleAfterMs: number | undefined;
}

export function AiCompositionSection({
  byKind,
  byRegion,
  byType,
  mcpDiscovered,
  mcpNotRunning,
  collectedAt,
  staleAfterMs,
}: AiCompositionSectionProps) {
  /* ── Kinds ─────────────────────────────────────────────────────────────
     Ordered by account count so the donut reads largest-first, but coloured
     from an alphabetical key list so the assignment is stable whatever order
     the payload arrives in. */
  const kindKeys = [...new Set(byKind.map((row) => row.kind))].sort((a, b) => a.localeCompare(b));
  const kindConfig = identityConfig(kindKeys);
  const rankedKinds = [...byKind].sort((a, b) => b.accounts - a.accounts);

  // ≤5 segments plus an explicit `Other` (§2). Folded rather than truncated, so
  // the donut's total still equals the estate.
  const headKinds = rankedKinds.slice(0, DONUT_LIMIT);
  const tailKinds = rankedKinds.slice(DONUT_LIMIT);
  const donutRows: { kind: string; accounts: number; isFold: boolean }[] = [
    ...headKinds.map((row) => ({ kind: row.kind, accounts: row.accounts, isFold: false })),
    ...(tailKinds.length > 0
      ? [
          {
            kind: "Other",
            accounts: tailKinds.reduce((sum, row) => sum + row.accounts, 0),
            isFold: true,
          },
        ]
      : []),
  ];
  const kindTotal = rankedKinds.reduce((sum, row) => sum + row.accounts, 0);

  /* ── Regions ───────────────────────────────────────────────────────────
     A ranking, so a horizontal bar sorted by value (§2). Truncation is stated
     in the caption and the whole list is one click away in the table twin. */
  const rankedRegions = [...byRegion].sort((a, b) => b.accounts - a.accounts);
  const shownRegions = rankedRegions.slice(0, REGION_LIMIT);

  /* ── Types ─────────────────────────────────────────────────────────────
     The discovery net. Sorted by count so an unexpected type is easy to spot. */
  const rankedTypes = [...byType].sort((a, b) => b.resources - a.resources);

  return (
    <section className="flex scroll-mt-6 flex-col gap-4">
      <SectionHeading
        id={COMPOSITION_ANCHOR}
        title="What the estate is made of"
        subtitle="Discovered by resource type and read as strings, never matched against a list of kinds someone typed in — so an account provisioned after this shipped appears here on its own. Deployment counts are nullable throughout: an em dash means the deployment list could not be read, not a kind with nothing deployed."
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard<AiKindCount>
          title="Accounts by ARM kind"
          caption={
            byKind.length === 0
              ? "Nothing discovered"
              : `${formatCount(byKind.length)} kind(s), discovered not enumerated`
          }
          since={collectedAt}
          staleAfterMs={staleAfterMs}
          tableTwin={{
            id: TWIN_IDS.byKind,
            ariaLabel: "AI service accounts and model deployments per ARM kind",
            data: rankedKinds,
            getRowId: (row) => row.kind,
            columns: [
              {
                id: "kind",
                header: "Kind",
                accessor: (row) => row.kind,
                cell: (row) => (
                  <span className="text-fg text-sm font-medium">
                    {row.kind}
                    {row.kind === UNKNOWN_KIND && (
                      <Badge tone="neutral" variant="outline" size="xs">
                        no kind reported
                      </Badge>
                    )}
                  </span>
                ),
              },
              {
                id: "accounts",
                header: "Accounts",
                align: "end",
                accessor: (row) => row.accounts,
                cell: (row) => <span className="tabular-nums">{formatCount(row.accounts)}</span>,
              },
              {
                id: "deployments",
                header: "Deployments",
                align: "end",
                accessor: (row) => row.deployments,
                cell: (row) => <ReadingCell reading={deploymentsReading(row.deployments)} />,
              },
            ],
          }}
        >
          {donutRows.length === 0 ? (
            <EmptyState
              size="sm"
              title="No AI account was discovered"
              description="The discovery query answered and returned nothing. That is a measured empty estate — not a failed read, which would have rendered the access panel instead."
            />
          ) : (
            <div className="flex flex-col gap-3">
              <div className="relative">
                <Chart.Container
                  config={kindConfig}
                  className="h-64"
                  aria-label={`AI service accounts by ARM kind across ${formatCount(byKind.length)} kinds`}
                >
                  <Chart.PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <Chart.Tooltip
                      content={
                        <Chart.TooltipContent
                          hideLabel
                          rows={(datum: (typeof donutRows)[number]) => [
                            { label: datum.kind, value: formatCount(datum.accounts) },
                            {
                              label: "Share of estate",
                              value:
                                kindTotal > 0
                                  ? formatPercent((datum.accounts / kindTotal) * 100)
                                  : formatPercent(null),
                            },
                          ]}
                        />
                      }
                    />
                    <Chart.Pie
                      {...Chart.seriesDefaults}
                      data={donutRows}
                      dataKey="accounts"
                      nameKey="kind"
                      cx="50%"
                      cy="50%"
                      innerRadius="60%"
                      outerRadius="85%"
                      paddingAngle={2}
                      cornerRadius={3}
                      stroke="var(--norican-surface)"
                      strokeWidth={2}
                    >
                      {donutRows.map((row) => (
                        <Chart.Cell
                          key={row.kind}
                          fill={
                            row.isFold || row.kind === UNKNOWN_KIND
                              ? MUTED_FILL
                              : (kindConfig[row.kind]?.color ?? MUTED_FILL)
                          }
                        />
                      ))}
                    </Chart.Pie>
                  </Chart.PieChart>
                </Chart.Container>
                {/* The hole carries the total, per the DS donut recipe. */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
                >
                  <Statistic
                    label="Accounts"
                    value={formatCount(kindTotal)}
                    size="lg"
                    className="text-center"
                  />
                </div>
              </div>
              {/* Identity segments name themselves, so the legend is labels not
                  icons — but the fold and the `unknown` bucket say what they are. */}
              <div className="text-fg-muted flex flex-wrap justify-center gap-3 text-xs">
                {donutRows.map((row) => (
                  <span key={row.kind} className="inline-flex items-center gap-1.5">
                    {row.kind} ({formatCount(row.accounts)})
                    {row.isFold && (
                      <Badge tone="neutral" variant="outline" size="xs">
                        {formatCount(tailKinds.length)} folded
                      </Badge>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

        <ChartCard<AiRegionCount>
          title="Accounts by region"
          caption={
            rankedRegions.length === 0
              ? "Nothing discovered"
              : rankedRegions.length > REGION_LIMIT
                ? `${formatOfTotal(shownRegions.length, rankedRegions.length)} regions — the rest are in the data view`
                : `${formatCount(rankedRegions.length)} region(s)`
          }
          since={collectedAt}
          staleAfterMs={staleAfterMs}
          tableTwin={{
            id: TWIN_IDS.byRegion,
            ariaLabel: "AI service accounts and model deployments per Azure region",
            data: rankedRegions,
            getRowId: (row) => row.location,
            columns: [
              { id: "location", header: "Region", accessor: (row) => row.location },
              {
                id: "accounts",
                header: "Accounts",
                align: "end",
                accessor: (row) => row.accounts,
                cell: (row) => <span className="tabular-nums">{formatCount(row.accounts)}</span>,
              },
              {
                id: "deployments",
                header: "Deployments",
                align: "end",
                accessor: (row) => row.deployments,
                cell: (row) => <ReadingCell reading={deploymentsReading(row.deployments)} />,
              },
            ],
          }}
        >
          {shownRegions.length === 0 ? (
            <EmptyState
              size="sm"
              title="No region reported an AI account"
              description="Nothing was discovered to place on a map. A failed read would have rendered the access panel instead."
            />
          ) : (
            <Chart.Container
              config={{ accounts: { label: "Accounts", color: "var(--norican-chart-2)" } }}
              className="h-64"
              aria-label={`AI service accounts per Azure region, top ${formatCount(shownRegions.length)} of ${formatCount(rankedRegions.length)}`}
            >
              <Chart.BarChart
                data={shownRegions}
                layout="vertical"
                margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
                <Chart.XAxis {...Chart.axisProps} type="number" allowDecimals={false} />
                <Chart.YAxis {...Chart.axisProps} type="category" dataKey="location" width={110} />
                <Chart.Tooltip
                  cursor={{ fill: "var(--norican-surface-hover)" }}
                  content={
                    <Chart.TooltipContent
                      rows={(datum: AiRegionCount) => [
                        { label: "Accounts", value: formatCount(datum.accounts) },
                        {
                          label: "Deployments",
                          value: deploymentsReading(datum.deployments).text,
                        },
                      ]}
                    />
                  }
                />
                <Chart.Bar
                  {...Chart.seriesDefaults}
                  dataKey="accounts"
                  fill="var(--color-accounts)"
                  radius={[0, 3, 3, 0]}
                />
              </Chart.BarChart>
            </Chart.Container>
          )}
        </ChartCard>
      </div>

      <PanelCard
        title="The discovery net"
        caption={
          rankedTypes.length === 0
            ? "No AI resource type answered"
            : `${formatCount(rankedTypes.length)} resource type(s) found under the AI provider prefixes`
        }
      >
        <div className="flex flex-col gap-3">
          {rankedTypes.length === 0 ? (
            <EmptyState
              size="sm"
              title="No resource answered under the AI provider prefixes"
              description="The census query returned nothing. Every count on this board is scoped to what it would have found."
            />
          ) : (
            <DataGrid<AiTypeCount>
              data={rankedTypes}
              getRowId={(row) => row.resource_type}
              density="compact"
              aria-label="Every AI resource type found under the provider prefixes, and how many of each"
              columns={[
                {
                  id: "type",
                  header: "ARM resource type",
                  accessor: (row) => row.resource_type,
                  sortable: true,
                  cell: (row) => <code className="text-fg text-xs">{row.resource_type}</code>,
                },
                {
                  id: "resources",
                  header: "Visible",
                  align: "end",
                  accessor: (row) => row.resources,
                  sortable: true,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.resources)}</span>,
                },
              ]}
            />
          )}
          <Typography variant="body-sm" tone="muted">
            This table is the net, not a curated list: it asks for every resource under the AI
            provider prefixes and reports whatever answers. A type nobody enumerated still reaches
            the board here, which is how a resource type this board cannot otherwise render becomes
            visible instead of silently absent.
          </Typography>
          <Typography variant="body-sm" tone="muted">
            {mcpDiscovered == null
              ? "MCP server discovery could not run in this snapshot, so how many exist is unknown — not zero. "
              : `${formatCount(mcpDiscovered)} MCP server(s) were identified from the control plane` +
                (mcpNotRunning == null
                  ? ", and how many are not running could not be read. "
                  : mcpNotRunning > 0
                    ? `, of which ${formatCount(mcpNotRunning)} are not reported as running. `
                    : ", all of them reported as running. ")}
            MCP is not an Azure resource type, so those are classified from tags and name patterns
            rather than discovered by type, which makes the count a classification result rather
            than a census: how many candidate hosts were examined to reach it, and what each one
            answered to an HTTP probe, is what the MCP fleet route reports — it has no page yet, so
            this line is deliberately not linked anywhere.
          </Typography>
        </div>
      </PanelCard>
    </section>
  );
}

/** Re-exported so the overview page can pair the section with its own icon set. */
export const COMPOSITION_ICON_HINT = Icon;
